/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useState } from 'react';
// @ts-ignore
import { Modal } from '@els/els-react--modal';
import {
    TextInput,
    ValidatedTextInput,
    ValidatedTextArea,
    // @ts-ignore
} from '@els/els-react--text-input';
// @ts-ignore
import { Icon } from '@els/els-react--icon';
// @ts-ignore
import { Button } from '@els/els-react--button';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectFirstName,
    selectLastName,
    selectClientFeatureFlags,
} from '../../features/login/login-selector';
import './email-modal.scss';
import { sendPatientEducationEmail } from './email-thunk';
import { FEATURE_FLAGS } from '../../../libs/utils/constants/feature-flag';

const EmailModal = (props: any) => {
    const { onCloseHandler, contentTitle, ecrId, lang, id, esid } = props;
    const dispatch = useDispatch();
    const baseClassName = 'email-modal';
    const firstName = useSelector(selectFirstName);
    const lastName = useSelector(selectLastName);
    const [recipientsEmail, setRecipientsEmail] = useState('');
    const [subject, setSubject] = useState(contentTitle || '');
    const defaultMessage = `${firstName || ''} ${
        lastName || ''
    } sent you this resource from Clinical Skills. Please use the link below to view the content.`;
    const [message, setMessage] = useState(defaultMessage);
    const [isInvalidEmail, setIsInvalidEmail] = useState(false);
    const MAX_EMAIL_LENGTH = 1024;
    const MAX_SUBJECT_LENGTH = 120;
    const [isSendButtonEnabled, setIsSendButtonEnabled] = useState(false);
    const clientFeatureFlags = useSelector(selectClientFeatureFlags);

    useEffect(() => {
        const isSubjectValid =
            subject.trim() !== '' && subject.length <= MAX_SUBJECT_LENGTH;
        const isEmailValid =
            recipientsEmail.length > 0 &&
            recipientsEmail.length <= MAX_EMAIL_LENGTH &&
            !isInvalidEmail;
        const isMessageValid = message.length <= MAX_EMAIL_LENGTH;

        setIsSendButtonEnabled(
            isSubjectValid && isEmailValid && isMessageValid
        );
    }, [subject, recipientsEmail, message]);

    const validateEmail = (emailEntered: string) => {
        const trimmedEmail = emailEntered.trim();

        if (!trimmedEmail) {
            setIsInvalidEmail(true);
            return emailEntered
                ? "Recipient's email cannot be only whitespace."
                : 'This field is required.';
        }

        const emails = trimmedEmail.split(/[,;]+/).map((email) => email.trim());
        const invalidEmails = emails.filter(
            (email) =>
                !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(email)
        );

        if (invalidEmails.length > 0) {
            setIsInvalidEmail(true);
            return 'Invalid email address.';
        }

        setIsInvalidEmail(false);
        return null;
    };

    const validateSubject = (subject: string) => {
        if (subject.trim() === '') {
            return 'Subject cannot be only whitespace.';
        }
        return null;
    };

    const handleRecipientsEmailChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRecipientsEmail(e.target.value);
    };

    const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSubject(e.target.value);
    };

    const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);
    };

    const onSendButtonClick = () => {
        const emailsList = recipientsEmail
            .trim()
            .split(/[,;]+/)
            .map((email) => email.trim())
            .map((email) => ({
                emailAddress: email,
                recipientName: email.split('@')[0],
            }));

        let newDate = new Date();
        const offset = newDate.getTimezoneOffset();
        newDate = new Date(newDate.getTime() - offset * 60 * 1000);
        const sendDate = newDate.toISOString().split('T')[0];

        const payload = {
            templateName: 'patient-education-email',
            emailsList,
            sendDate,
            metadata: {
                useRaven:
                    clientFeatureFlags[FEATURE_FLAGS.ENABLE_EMAIL_FROM_RAVEN],
                patientEducationTitle: contentTitle || subject,
                patientEducationEmailTitle: subject,
                emailMessage: message,
                ecrId,
                languageCode: lang,
                id,
                esid,
            },
            requestId: null,
        };

        dispatch(sendPatientEducationEmail(payload));
        onCloseHandler();
    };

    return (
        <Modal
            onClose={onCloseHandler}
            shouldCloseOnEscape
            shouldPreventClickOverlayToClose={false}
            shouldHideCloseButton
            className={baseClassName}
            type="neutral"
        >
            <div className={`${baseClassName}__container`}>
                <div className={`${baseClassName}__container-wrapper`}>
                    <h3 className={`${baseClassName}__container-header`}>
                        Email
                    </h3>
                    <Button
                        onClick={onCloseHandler}
                        className={`${baseClassName}__container_wrapper_close-btn`}
                        type="link"
                    >
                        <Icon
                            sprite="Close"
                            size="s"
                            isVisible
                            a11y={{ name: 'Close' }}
                        />
                    </Button>
                </div>
                <div className={`${baseClassName}__email-body`}>
                    <div className={`${baseClassName}__email-body_wrapper`}>
                        <ValidatedTextInput
                            label="Recipient's email"
                            value={recipientsEmail}
                            size="medium"
                            required
                            onChange={handleRecipientsEmailChange}
                            max={1024}
                            className="email-modal-recipient-email"
                            messages={{
                                MAX_ERROR_MESSAGE: `Entry must be ${MAX_EMAIL_LENGTH} characters or fewer.`,
                            }}
                            validator={validateEmail}
                        />
                        {!isInvalidEmail &&
                            recipientsEmail.length <= MAX_EMAIL_LENGTH && (
                                <TextInput.Message
                                    type={TextInput.Message.Types.DEFAULT}
                                    id="recipient-email-info-message"
                                >
                                    Separate multiple addresses with commas or
                                    semicolons.
                                </TextInput.Message>
                            )}
                        <ValidatedTextInput
                            label="Subject"
                            value={subject}
                            size="medium"
                            required
                            onChange={handleSubjectChange}
                            max={120}
                            className="email-modal-recipient-subject"
                            messages={{
                                MAX_ERROR_MESSAGE: `Entry must be ${MAX_SUBJECT_LENGTH} characters or fewer.`,
                            }}
                            validator={validateSubject}
                        />
                        <ValidatedTextArea
                            value={message}
                            label="Message"
                            rows={5}
                            onChange={handleMessageChange}
                            className="email-modal-recipient-message"
                            max={MAX_EMAIL_LENGTH}
                            messages={{
                                MAX_ERROR_MESSAGE: `Entry must be ${MAX_EMAIL_LENGTH} characters or fewer.`,
                            }}
                            validator="none"
                        />
                    </div>
                </div>
                <div className={`${baseClassName}__button_group`}>
                    <Button
                        className={`${baseClassName}__button_group-send-button`}
                        onClick={onSendButtonClick}
                        id="send-email"
                        disabled={!isSendButtonEnabled}
                    >
                        Send
                    </Button>
                    <Button
                        className={`${baseClassName}__button_group-cancel-button`}
                        onClick={onCloseHandler}
                        id="cancel-email"
                        type="secondary"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default EmailModal;
